<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute } from 'vue-router'

import { layouts } from './layouts'
import type { LayoutName } from './router/meta'
import { usePageTitle } from './shell/usePageTitle'
import { useQueryCallbacks } from './shell/useQueryCallbacks'
import { useVersionGate } from './shell/useVersionGate'

import { OnboardingHost } from '@/features/global-surface'
import { provideUiLabels } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'
import { ErrorBoundary } from '@/shared/ui/error-boundary'
import { ConfirmHost, ToastHost } from '@/shared/ui/feedback'
import { useTheme, useDirection } from '@/shared/ui/theme'

const { t } = useI18n()
const route = useRoute()
const { resolvedTheme } = useTheme()
const { direction } = useDirection()
const activeLayout = computed(() => layouts[(route.meta.layout ?? 'app') as LayoutName])

// Resolve the reserved `ui.*` labels once at the root so `shared/ui` primitives inject localised
// copy without importing vue-i18n (contract 04 §9, §18).
provideUiLabels()

usePageTitle()
useVersionGate()
// Root-owned: auth handoffs arrive on AuthLayout, while account/billing callbacks arrive on
// AppLayout. One mount here consumes both without double-running any registered handler.
useQueryCallbacks()

// Stamp theme + direction on <html> for the app's lifetime (RTL-ready via logical CSS).
watchEffect(() => {
  const root = document.documentElement
  root.dataset.theme = resolvedTheme.value
  root.dir = direction.value
})
</script>

<template>
  <ErrorBoundary>
    <RouterView v-slot="{ Component }">
      <component :is="activeLayout">
        <component :is="Component" />
      </component>
    </RouterView>
    <template #fallback="{ reset }">
      <div class="app-error grid min-h-dvh place-items-center gap-md font-sans" role="alert">
        <p class="m-0 text-fg">{{ t('common.somethingWentWrong') }}</p>
        <Button variant="outline" @click="reset">{{ t('common.tryAgain') }}</Button>
      </div>
    </template>
  </ErrorBoundary>
  <!-- Above every layout, so a mandatory run covers whatever page the account landed on. -->
  <OnboardingHost />
  <ToastHost />
  <ConfirmHost />
</template>
